import { ArrowRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LaunchPromoPrice } from './LaunchPromoPrice'

export type JoinCtaButtonProps = {
  children: ReactNode
  to?: string
  size?: 'md' | 'lg' | 'xl'
  variant?: 'primary' | 'secondary' | 'ghost'
  /** Jasne tło strony lub ciemne hero. */
  tone?: 'light' | 'dark'
  /** Podpis pod przyciskiem; domyślnie wyłączony (cena w banerze u góry). */
  hint?: string | false
  showPriceBadge?: boolean
  fullWidth?: boolean
  className?: string
  onClick?: () => void
}

const sizeClasses = {
  md: 'gap-1.5 px-4 py-2.5 text-sm md:gap-2 md:px-6 md:py-3.5 md:text-base',
  lg: 'gap-2 px-5 py-3 text-sm md:gap-2.5 md:px-8 md:py-4 md:text-lg',
  xl: 'gap-2 px-5 py-3 text-sm md:gap-3 md:px-10 md:py-5 md:text-xl',
} as const

function buttonSurface(
  variant: JoinCtaButtonProps['variant'],
  tone: JoinCtaButtonProps['tone'],
): string {
  if (variant === 'ghost') {
    return tone === 'dark'
      ? 'text-white/90 hover:text-white'
      : 'text-ink hover:text-ink/80'
  }
  if (variant === 'secondary') {
    return tone === 'dark'
      ? 'pds-btn-outline pds-btn-outline--on-dark'
      : 'pds-btn-outline'
  }
  return 'join-cta-primary bg-brand text-brand-ink shadow-[0_14px_40px_rgba(241,201,1,0.35)] hover:brightness-[1.03] active:scale-[0.99]'
}

export function JoinCtaButton({
  children,
  to = '/formularz',
  size = 'lg',
  variant = 'primary',
  tone = 'light',
  hint = false,
  showPriceBadge = false,
  fullWidth = false,
  className = '',
  onClick,
}: JoinCtaButtonProps) {
  const resolvedHint = hint === false ? null : hint

  const isGhost = variant === 'ghost'
  const shared =
    'group inline-flex items-center justify-center font-extrabold tracking-tight transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/70 focus-visible:ring-offset-2 ' +
    (tone === 'dark' ? 'focus-visible:ring-offset-[#010218]' : 'focus-visible:ring-offset-paper') +
    (fullWidth ? ' w-full' : '') +
    (isGhost ? '' : variant === 'primary' ? ' rounded-lg' : '') +
    ` ${sizeClasses[size]} ${buttonSurface(variant, tone)}` +
    (className ? ` ${className}` : '')

  const label = (
    <>
      <span className="inline-flex shrink-0 items-center justify-center gap-x-2 whitespace-nowrap text-center">
        <span>{children}</span>
        {showPriceBadge ? (
          <span
            className={
              'rounded-lg px-2.5 py-0.5 text-[0.72em] font-bold ' +
              (variant === 'primary'
                ? 'bg-brand-ink/10 text-brand-ink'
                : tone === 'dark'
                  ? 'bg-white/15 text-white'
                  : 'bg-ink/8 text-ink')
            }
          >
            <LaunchPromoPrice tone={tone === 'dark' ? 'onDark' : 'default'} />
          </span>
        ) : null}
      </span>
      {!isGhost ? <ArrowRight className="h-[1.1em] w-[1.1em] shrink-0 transition group-hover:translate-x-0.5" aria-hidden /> : null}
    </>
  )

  const isHashLink = to.startsWith('#')

  const control = onClick ? (
    <button type="button" onClick={onClick} className={shared}>
      {label}
    </button>
  ) : isHashLink ? (
    <a href={to} className={shared}>
      {label}
    </a>
  ) : (
    <Link to={to} className={shared}>
      {label}
    </Link>
  )

  if (!resolvedHint) return control

  return (
    <div
      className={
        (fullWidth ? 'w-full items-stretch' : 'inline-flex items-start') +
        ' flex flex-col gap-2'
      }
    >
      {control}
      {resolvedHint ? (
        <p
          className={
            'max-w-[40ch] text-[13px] font-medium leading-snug md:text-sm ' +
            (tone === 'dark' ? 'text-white/75' : 'text-ink/55') +
            (fullWidth ? ' text-center' : '')
          }
        >
          {resolvedHint}
        </p>
      ) : null}
    </div>
  )
}
